import { STEP1_LIFE_STORY_PROMPTS } from '../config/step1LifeStoryPrompts';
import { Step1WizardAnswerEntry } from '../types/step1Writing';
import { formatAloudDurationLabel } from './readTimeEstimate';
import { getActiveLocale, t } from '../i18n/translate';

/** Suggest trimming when draft exceeds this word count (~20+ min at slowest pace) */
export const LONG_DRAFT_CHECKLIST_WORD_THRESHOLD = 3500;

export interface DraftChecklistItem {
  id: string;
  severity: 'info' | 'suggestion' | string;
  feedback: string;
  recommendation: string;
  promptId?: string;
}

function blocksFromAnswers(wizardAnswers: Record<string, Step1WizardAnswerEntry>): string[] {
  const blocks: string[] = [];
  for (const prompt of STEP1_LIFE_STORY_PROMPTS) {
    const entry = wizardAnswers[prompt.id];
    if (!entry || entry.status === 'skipped' || entry.status === 'na' || entry.status === 'covered_elsewhere') {
      continue;
    }
    const text = (entry.examples || []).map((e) => e.trim()).filter(Boolean).join('\n\n');
    if (text) blocks.push(text);
  }
  return blocks;
}

/** Verbatim assembly — user words only, paragraphs separated by blank lines */
export function assembleVerbatimDraft(wizardAnswers: Record<string, Step1WizardAnswerEntry>): string {
  return blocksFromAnswers(wizardAnswers).join('\n\n');
}

export function blocksForFlowWeave(wizardAnswers: Record<string, Step1WizardAnswerEntry>): string[] {
  return blocksFromAnswers(wizardAnswers);
}

/** Join walkthrough answer blocks with blank lines */
export function localFlowWeave(blocks: string[]): string {
  return blocks.map((b) => b.trim()).filter(Boolean).join('\n\n');
}

/** Non-AI keyword checklist for common First Step topics */
export function localDraftChecklist(
  contentText: string,
  _wizardAnswers?: Record<string, Step1WizardAnswerEntry>
): DraftChecklistItem[] {
  const issues: DraftChecklistItem[] = [];
  const text = contentText.trim();
  const lower = text.toLowerCase();

  if (!text) {
    return issues;
  }

  if (!lower.includes('sex addict') && !lower.includes('sexual addict')) {
    issues.push({
      id: 'introduction',
      severity: 'suggestion',
      feedback: t('step.1.checklist.introduction.feedback'),
      recommendation: t('step.1.checklist.introduction.recommendation'),
      promptId: 'intro_name',
    });
  }

  if (!lower.includes('powerless') && !lower.includes('could not stop') && !lower.includes("couldn't stop")) {
    issues.push({
      id: 'powerlessness',
      severity: 'suggestion',
      feedback: t('step.1.checklist.powerlessness.feedback'),
      recommendation: t('step.1.checklist.powerlessness.recommendation'),
      promptId: 'powerlessness_moment',
    });
  }

  if (!lower.includes('unmanageable') && !lower.includes('consequence') && !lower.includes('damage')) {
    issues.push({
      id: 'unmanageability',
      severity: 'suggestion',
      feedback: t('step.1.checklist.unmanageability.feedback'),
      recommendation: t('step.1.checklist.unmanageability.recommendation'),
      promptId: 'unmanageability_overview',
    });
  }

  const wordCount = text.split(/\s+/).filter(Boolean).length;
  if (wordCount < 200) {
    issues.push({
      id: 'length',
      severity: 'info',
      feedback: t('step.1.checklist.length.feedback', {
        count: wordCount.toLocaleString(getActiveLocale()),
      }),
      recommendation: t('step.1.checklist.length.recommendation'),
    });
  }

  if (wordCount > LONG_DRAFT_CHECKLIST_WORD_THRESHOLD) {
    issues.push({
      id: 'length_long',
      severity: 'info',
      feedback: t('step.1.checklist.length_long.feedback', {
        count: wordCount.toLocaleString(getActiveLocale()),
        aloud: formatAloudDurationLabel(wordCount),
      }),
      recommendation: t('step.1.checklist.length_long.recommendation'),
    });
  }

  return issues.slice(0, 4);
}

/** Stable fingerprint of walkthrough answers — used to detect when re-assembly is needed. */
export function fingerprintWizardAnswers(
  wizardAnswers: Record<string, Step1WizardAnswerEntry>
): string {
  const parts = STEP1_LIFE_STORY_PROMPTS.map((prompt) => {
    const entry = wizardAnswers[prompt.id];
    const examples = (entry?.examples || []).map((e) => e.trim());
    const status = entry?.status || (examples.some(Boolean) ? 'answered' : 'empty');
    return `${prompt.id}:${status}:${examples.join('\x1e')}`;
  });
  return parts.join('\x1f');
}
